import { useCallback, useMemo } from "react";

import { useLibrary } from "../../../providers";
import { useDeepEqualMemo, useObjectMemo } from "../../../hooks";
import { useUserAlerts } from "../../entities/alerts";
import { useFeature } from "../features";

import {
  ALERT_CONFIGURATIONS,
  ALERT_SECTIONS,
  DUE_DATE_OPTIONS,
  LOAN_ACCOUNTS,
  MOBILE_DELIVERY_CHANNELS,
  WEB_DELIVERY_CHANNELS,
} from "./constants";

import type Account from "../../../../Account";
import type {
  AlertConfigurableField,
  AlertSection,
  DeliveryChannelConfiguration,
  DueDateOption,
} from "./types";

function snakeToKebabCase(str: string): string {
  return str.replace(/_/g, "-");
}

export const useHasLoanAccount = (
  accounts: API.Account[] | Account[],
): boolean => {
  return useMemo(
    () =>
      accounts.some((account) => LOAN_ACCOUNTS.includes(account.product.type)),
    [accounts],
  );
};

export const useAlertDeliveryChannels = (
  platform: "mobile-app" | "web",
  disabledChannels: API.DeliveryChannel[] = [],
): DeliveryChannelConfiguration[] => {
  const t = useLibrary("translations");
  const { featureEnabled } = useFeature();

  return useMemo(() => {
    const deliveryChannels: DeliveryChannelConfiguration[] =
      platform === "mobile-app"
        ? MOBILE_DELIVERY_CHANNELS
        : WEB_DELIVERY_CHANNELS;

    // Removes push notifications as an option if push
    // notifications are not enabled by the FI.
    const filteredDeliveryChannels: DeliveryChannelConfiguration[] =
      deliveryChannels.filter(({ type }) =>
        !featureEnabled("push_notifications") ? type !== "push" : true,
      );

    // Translate delivery channel name and mark appropriate channels as disabled.
    return filteredDeliveryChannels.map((channel) => ({
      type: channel.type,
      name: t.getString(`alerts-delivery-channel-${channel.type}`),
      icon: channel.icon,
      disabled: disabledChannels.includes(channel.type),
    }));
  }, [disabledChannels, featureEnabled, platform, t]);
};

export const useAlertConfiguration = (
  key: API.AlertKey,
): AlertConfigurableField[] => {
  const t = useLibrary("translations");

  return useMemo(() => {
    return (
      ALERT_CONFIGURATIONS[key]?.map(
        ({
          fieldSectionTitle,
          fieldKey,
          fieldLabel,
          showSectionTitle,
          ...config
        }) => {
          const sectionTitle = showSectionTitle
            ? {
                fieldSectionTitle: t.getString(
                  `enhanced-alerts-field-section-title-${fieldKey}`,
                ),
              }
            : { fieldSectionTitle: "" };

          return {
            ...config,
            fieldKey,
            showSectionTitle,
            fieldLabel: t.getString(`enhanced-alerts-field-label-${fieldKey}`),
            ...sectionTitle,
          } as AlertConfigurableField;
        },
      ) || []
    );
  }, [key, t]);
};

type AlertStrings = {
  title: string;
  description: string;
  accessibilityLabel: string;
  accessibilityHint: string;
};

type AlertDescriptions = Partial<Record<API.AlertKey, AlertStrings>>;

const ALERT_KEYS: API.AlertKey[] = [
  "credit",
  "debit",
  "low_available_balance",
  "available_balance",
  "loan_reminder",
  "loan_overdue",
];

export const useTranslatedAlerts = (): AlertDescriptions => {
  const t = useLibrary("translations");
  const translatedDescriptions: AlertDescriptions = {};

  ALERT_KEYS.forEach((alertKey) => {
    translatedDescriptions[alertKey] = {
      title: t.getString(`enhanced-alert-title-${snakeToKebabCase(alertKey)}`),
      description: t.getString(
        `enhanced-alert-description-${snakeToKebabCase(alertKey)}`,
      ),
      accessibilityLabel: t.getString(
        `enhanced-alert-accessibility-label-${snakeToKebabCase(alertKey)}`,
      ),
      accessibilityHint: t.getString(
        `enhanced-alert-accessibility-hint-${snakeToKebabCase(alertKey)}`,
      ),
    };
  });

  return translatedDescriptions || {};
};

export const useTranslatedAlert = (key: API.AlertKey): AlertStrings => {
  const translatedAlerts = useTranslatedAlerts();
  return useObjectMemo(
    translatedAlerts[key] ?? {
      title: "",
      description: "",
      accessibilityLabel: "",
      accessibilityHint: "",
    },
  );
};

export const useAlertSections = (
  accounts: API.Account[] | Account[],
): AlertSection[] => {
  const t = useLibrary("translations");
  const alerts = useUserAlerts() || [];
  const hasLoanAccount = useHasLoanAccount(accounts);

  const { featureEnabled } = useFeature();
  const translatedAlerts = useTranslatedAlerts();

  return useDeepEqualMemo((): AlertSection[] => {
    return ALERT_SECTIONS.map(({ sectionKey, items }) => ({
      section: t.getString(`enhanced-alert-section-title-${sectionKey}`),
      sectionKey,
      items: items
        .filter((item) => {
          if (
            // `featureflag` provided and feature disabled.
            item.featureFlag !== undefined &&
            !featureEnabled(item.featureFlag)
          ) {
            return false;
          }
          if (
            // Has no loan account and item is loan related.
            !hasLoanAccount &&
            ["loan_reminder", "loan_overdue"].includes(item.key)
          ) {
            return false;
          }
          return true;
        })
        .map((item) => {
          const existingAlert = alerts.find(
            (alertInfo) => alertInfo.alert_key === item.key,
          );
          return {
            ...item,
            alertName: translatedAlerts[item.key]?.title || item.alertName,
            isActive: !!existingAlert,
            alert: existingAlert,
          };
        }),
    }));
  }, [alerts, featureEnabled, t]);
};

export const useTranslatedDueDateOptions = (): DueDateOption[] => {
  const t = useLibrary("translations");

  const getDisplayValue = useCallback(
    (daysBefore: number) => {
      switch (daysBefore) {
        case 0:
          return t.getString("enhanced-alert-option-label-due-date-0");
        case 1:
          return t.getString("enhanced-alert-option-label-due-date-1");
        default:
          return t.getString("enhanced-alert-option-label-due-date-x", {
            daysBefore,
          });
      }
    },
    [t],
  );

  return useDeepEqualMemo(() => {
    return DUE_DATE_OPTIONS.map((option) => ({
      ...option,
      displayValue: getDisplayValue(option.id),
    }));
  }, [getDisplayValue]);
};
